import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { APP_VERSION, APP_HOSTNAME, API_URL } from '../../runtimeConfig';
import { cn } from '../../lib/cn';
import { Tooltip } from '../ui/tooltip';

const API_ENDPOINTS = [
  { key: 'api-service', label: 'Service' },
  { key: 'api-iam', label: 'IAM' },
  { key: 'api-domain', label: 'Domain' },
  { key: 'api-website', label: 'Website' },
  { key: 'api-container', label: 'Container' },
  { key: 'api-vps', label: 'VPS' },
  { key: 'api-order', label: 'Order' },
  { key: 'api-email', label: 'Email' },
  { key: 'api-billing', label: 'Billing' },
  { key: 'api-support', label: 'Support' },
  { key: 'api-status', label: 'Status' },
  { key: 'api-database', label: 'Database' },
  { key: 'api-messaging', label: 'Messaging' },
  { key: 'api-emailing', label: 'Emailing' },
  { key: 'api-whatsapp', label: 'WhatsApp' },
  { key: 'api-ai', label: 'AI' },
  { key: 'api-changelog', label: 'Changelog' },
];

interface ApiStatus {
  label: string;
  key: string;
  up: boolean;
  enabled: boolean;
  version: string;
  hostname: string;
}

async function fetchAPIStatuses(): Promise<ApiStatus[]> {
  const results = await Promise.allSettled(
    API_ENDPOINTS.map(async ({ key, label }) => {
      try {
        const res = await fetch(`${API_URL}/${key}/health`, { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          return {
            label,
            key,
            up: true,
            enabled: data.enabled !== false,
            version: data.version || '',
            hostname: data.hostname || '',
          };
        }
        return { label, key, up: false, enabled: true, version: '', hostname: '' };
      } catch {
        return { label, key, up: false, enabled: true, version: '', hostname: '' };
      }
    })
  );
  return results.map((r) =>
    r.status === 'fulfilled'
      ? r.value
      : { label: '?', key: '?', up: false, enabled: true, version: '', hostname: '' }
  );
}

interface ApiStatusIndicatorProps {
  className?: string;
  variant?: 'dot' | 'pill';
}

export function ApiStatusIndicator({ className, variant = 'pill' }: ApiStatusIndicatorProps) {
  const [open, setOpen] = useState(false);
  const [statuses, setStatuses] = useState<ApiStatus[]>([]);
  const fetched = useRef(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const load = async () => {
    if (fetched.current) return;
    fetched.current = true;
    const result = await fetchAPIStatuses();
    setStatuses(result);
  };

  useEffect(() => {
    load();
    const id = window.setInterval(() => {
      fetched.current = false;
      load();
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Click-to-toggle popover: outside-click and Escape close it. We watch
  // `mousedown` on document instead of `click` so the same mousedown that
  // happens to land on the trigger doesn't both close (via outside handler)
  // and reopen (via the trigger's onClick).
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = statuses.filter((s) => s.enabled);
  const upCount = visible.filter((s) => s.up).length;
  const total = visible.length || API_ENDPOINTS.length;
  const allUp = visible.length > 0 && upCount === total;
  const anyDown = visible.length > 0 && upCount < total;

  const toggle = () => setOpen((v) => !v);

  return (
    <span ref={containerRef} className={cn('relative inline-flex', className)}>
      {variant === 'dot' ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="dialog"
          className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-accent"
          aria-label="API status"
        >
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              statuses.length === 0 && 'bg-muted-foreground animate-pulse',
              allUp && 'bg-success',
              anyDown && 'bg-destructive'
            )}
          />
        </button>
      ) : (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="dialog"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent"
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              statuses.length === 0 && 'bg-muted-foreground animate-pulse',
              allUp && 'bg-success',
              anyDown && 'bg-destructive'
            )}
          />
          <span className="font-medium">
            {statuses.length === 0 ? 'Checking…' : allUp ? 'All services up' : `${upCount}/${total} services up`}
          </span>
        </button>
      )}

      {open && (
        // Mobile: fixed-position, full-viewport-wide minus 16px gutter — anchored
        // to the topbar height so it never overflows. Desktop (sm+): the
        // original tight popover anchored to the trigger.
        <div className="fixed top-14 left-2 right-2 sm:absolute sm:top-full sm:left-auto sm:right-0 sm:mt-2 sm:w-80 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-elevation-3 z-50 animate-fade-in">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Service health</p>
            <span className="text-[10px] text-muted-foreground">{upCount}/{total} up</span>
          </div>
          <ul className="space-y-1 max-h-64 overflow-auto pr-3 [scrollbar-gutter:stable]">
            {(statuses.length
              ? visible
              : API_ENDPOINTS.map((e) => ({
                  label: e.label,
                  key: e.key,
                  up: false,
                  enabled: true,
                  version: '',
                  hostname: '',
                }))
            ).map((api) => (
              <li key={api.key} className="flex items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-2 min-w-0">
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', api.up ? 'bg-success' : 'bg-destructive/70')} />
                  <span className="text-foreground truncate">API {api.label}</span>
                </span>
                {api.up && (
                  <span className="text-muted-foreground text-[10px] truncate">
                    {api.version}
                    {api.hostname ? ` · ${api.hostname}` : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2 border-t border-border pt-2 text-[10px] text-muted-foreground">
            Dashboard {APP_VERSION}
            {APP_HOSTNAME ? ` · ${APP_HOSTNAME}` : ''}
          </div>
        </div>
      )}
    </span>
  );
}

export function AppFooter({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-1 text-[10px] leading-tight text-muted-foreground', className)}>
      <div>
        Dashboard <span className="font-medium">{APP_VERSION}</span>
        {APP_HOSTNAME ? ` · ${APP_HOSTNAME}` : ''}
      </div>
      <Tooltip content="Live service health" side="top">
        <div className="cursor-help">&copy; {new Date().getFullYear()} EDJ-LABS</div>
      </Tooltip>
    </div>
  );
}
